export const contentGenerator = {
  pageTitle: 'Content Generator',
  pageDescription: 'Configure your data, generate a brief, and create your optimized article.',
  generateButton: 'Generate Article',
  generating: 'Generating article...',
  progress: {
    label: 'Progress',
    basics: 'Data',
    brandVoice: 'Brand Voice',
    brief: 'Brief',
  },
  sections: {
    content: 'Content',
    seo: 'SEO & Entity',
    brandVoice: 'Brand Voice',
    images: 'Images',
    sources: 'Sources & References',
    advanced: 'Advanced Settings',
    subtitles: {
      images: 'Choose between stock photos or AI-generated images',
      sources: 'Add sources to improve content accuracy',
      advanced: 'Tags, fact updates, and custom instructions',
    },
    badges: {
      required: 'Required',
      optional: 'Optional',
      recommended: 'Recommended',
      ready: 'Ready',
    },
  },
  contentSource: {
    prompt: 'Write content',
    promptDescription: 'Configure data directly',
    video: 'Import from video',
    videoDescription: 'YouTube or TikTok',
    text: 'Ask AI',
    textDescription: 'Chat with AI',
  },
  validation: {
    required: 'This field is required',
  },
  cta: {
    ready: 'All set',
    readyHint: 'Your article is configured',
    completeBasics: 'Complete basic information',
    completeBasicsHint: 'Title, keyword, entity, and approach',
    writeBrief: 'Write the brief',
    writeBriefHint: 'Generate or write the content brief',
    selectBrandVoice: 'Select a Brand Voice first',
  },
  briefSection: {
    disabledMessage: 'Complete required fields above first',
  },
  contentDataStep: {
    language: {
      label: 'Article language',
      placeholder: 'Select a language',
      options: {
        es: 'Spanish',
        en: 'English',
        pt: 'Portuguese',
      },
    },
    title: {
      label: 'Article title',
      placeholder: 'How to protect your company from digital payment fraud',
    },
    mainKeyword: {
      label: 'Main keyword',
      placeholder: 'digital payment fraud',
    },
    secondaryKeywords: {
      label: 'Secondary keywords',
      placeholder: 'fraud prevention, PCI DSS, tokenization',
      helper: 'Separated by comma',
    },
    mainEntity: {
      label: 'Main entity',
      placeholder: 'Payment security',
      helper: 'The main topic or concept the content revolves around',
    },
    contentApproach: {
      label: 'Content approach',
      placeholder: 'Educational article aimed at CTOs and security managers of B2B companies in Latin America about fraud prevention.',
      helper: 'Describe the approach and structure of the content (minimum {{min}} characters)',
    },
    questionsToAnswer: {
      label: 'Questions to answer',
      placeholder: 'What are the most common frauds?, How to implement multi-factor authentication?',
      helper: 'Questions the content should answer, separated by comma',
    },
    referenceSources: {
      label: 'Reference URLs',
      placeholder: 'https://www.pcisecuritystandards.org, https://www.visa.com/security',
      helper: 'Source URLs to consider, separated by comma',
    },
    customPrompt: {
      label: 'Custom instructions',
      placeholder: 'Ex: Start all articles with a metric, Include 5 FAQs at the end',
      helper: 'Additional instructions to customize content generation (optional)',
    },
    images: {
      title: 'Images',
      count: 'Number of images',
      countHelper: '(0-5 images)',
      sourceLabel: 'Image source',
      sourceStock: 'Stock Photos',
      sourceAi: 'AI Generated',
      stockHelper: 'Images will be sourced from Magnific stock photos based on your content title and sections',
      aiFallbackLabel: 'Use AI images if stock is unavailable',
      aiFallbackHelper:
        'If the stock provider fails, AI-generated images are used instead. Turn off to leave the content without images.',
      prompt: 'Section images style (optional)',
      promptPlaceholder: 'Ex: Minimalist style, corporate colors',
      promptHelper: 'Applied to section images. If not specified, a style based on content will be generated',
      heroPrompt: 'Hero image style (optional)',
      heroPromptPlaceholder: 'Ex: Cinematic, wide panoramic, bold colors',
      heroPromptHelper: 'Specific instructions for the cover/hero image. Falls back to section style if empty',
      styleLabel: 'Visual style',
      styleHelper: 'Choose the visual style for generated images',
      styles: {
        illustrative: 'Illustrative',
        photographic: 'Photographic',
        infographic: 'Infographic',
        abstract: 'Abstract',
        'flat-design': 'Flat Design',
      },
      styleDescriptions: {
        illustrative: 'Hand-drawn or digital illustrations',
        photographic: 'Realistic photographic style',
        infographic: 'Data-driven visual diagrams',
        abstract: 'Abstract shapes and compositions',
        'flat-design': 'Clean, minimal flat vectors',
      },
    },
    referenceImages: {
      label: 'Reference images',
      empty: 'No reference images uploaded.',
      goToSettings: 'Upload in Brand Identity',
    },
    website: {
      label: 'Website for Interlinking',
      placeholder: 'Select website',
      helper: 'Internal links will be searched on this website',
      empty: 'No websites configured.',
      goToSettings: 'Add',
    },
    factUpdate: {
      title: 'Fact Update',
      description: 'Searches the web for updated data (prices, statistics, dates) and replaces them in the content.',
      toggle: 'Enable fact update',
      timeframe: {
        label: 'Search data from',
        options: {
          last_24h: 'Last 24 hours',
          last_week: 'Last week',
          last_month: 'Last month',
          last_3_months: 'Last 3 months',
          last_year: 'Last year',
        },
      },
    },
    folder: {
      title: 'Folder',
      description: 'Organize this content into a folder.',
      assign: 'Assign folder',
      change: 'Change',
      searchFolders: 'Search folders...',
      noFoldersFound: 'No results',
      empty: 'No folders yet. Create one below.',
      createNew: 'Create new folder',
      namePlaceholder: 'Folder name',
      color: 'Color:',
      cancel: 'Cancel',
      create: 'Create',
      alreadyExists: 'A folder with that name already exists',
    },
    tags: {
      title: 'Tags',
      description: 'Assign tags to organize and filter your content.',
      empty: 'No tags available. Create one with the button below.',
      selected: '{{count}} tags selected',
      addTag: 'Add tag',
      searchTags: 'Search tags...',
      noTagsFound: 'No results',
      createNewTag: 'Create new tag',
      tagNamePlaceholder: 'Tag name',
      tagColor: 'Color:',
      cancel: 'Cancel',
      create: 'Create',
      alreadyExists: 'A tag with that name already exists',
    },
  },
  brandVoiceSelector: {
    label: 'Brand Voice',
    placeholder: 'Select a brand voice',
    createNew: 'Create New',
    noBrandVoices: 'No brand voices found. Create your first brand voice to continue.',
    loading: 'Loading brand voices...',
    summary: {
      voiceTone: 'Voice tone',
      audience: 'Audience',
      dos: '{{count}} dos',
      donts: "{{count}} don'ts",
    },
  },
  briefPreviewStep: {
    generateDescription: 'Automatically generate a brief based on the data and brand voice you configured',
    generateButton: 'Generate Brief',
    generating: 'Generating...',
    brief: {
      label: 'Content brief',
      placeholder: 'The brief will appear here after generating it. You can also write it manually.',
      helperMin: 'Minimum {{min}} characters to continue',
      helperReady: 'This brief will guide the article generation',
    },
    info: {
      title: 'Information',
      item1: 'The brief summarizes the structure and approach of the article',
      item2: 'You can edit the generated brief to adjust it to your needs',
      item3: 'A more detailed brief produces better results',
    },
  },
  generationStep: {
    summary: {
      contentData: 'Content Data',
      brandVoice: 'Brand Voice',
      brief: 'Brief',
      tags: 'Tags',
      title: 'Title:',
      mainKeyword: 'Main keyword:',
      entity: 'Entity:',
      secondaryKeywords: 'Secondary keywords:',
      tone: 'Tone:',
      pointOfView: 'Point of view:',
      archetype: 'Archetype:',
      characters: '{{count}} characters',
    },
    images: {
      title: 'Images',
      count: 'Number of images',
      countHelper: '(0-5 images)',
      sourceLabel: 'Image source',
      sourceStock: 'Stock Photos',
      sourceAi: 'AI Generated',
      stockHelper: 'Images will be sourced from Magnific stock photos based on your content title and sections',
      prompt: 'Section images style (optional)',
      promptPlaceholder: 'Ex: Minimalist style, corporate blue and white colors, professional illustrations',
      promptHelper: 'Applied to section images. If not specified, a style based on the article content will be generated',
      heroPrompt: 'Hero image style (optional)',
      heroPromptPlaceholder: 'Ex: Cinematic, wide panoramic, bold colors',
      heroPromptHelper: 'Specific instructions for the cover/hero image. Falls back to section style if empty',
      style: 'Visual style',
    },
    generateButton: 'Generate Article',
    generating: 'Generating article...',
    loading: {
      title: 'Generating your article...',
      goToContentsHint: 'This may take a few minutes. You don\'t have to wait here.',
      goToContents: 'Go to My Contents',
      createNew: 'Create new content',
      steps: {
        analyzing: 'Analyzing brief...',
        searching: 'Searching the web...',
        processing: 'Processing sources...',
        writing: 'Writing article...',
        updatingFacts: 'Updating facts...',
        verifying: 'Verifying quality...',
      },
    },
    error: {
      title: 'Generation failed',
    },
    result: {
      title: 'Your article is ready!',
    },
  },
  required: '*',
  verification: {
    summary: '{{passed}} of {{total}} criteria met',
    allPassed: 'All criteria met',
    needsImprovement: {
      title: 'Needs improvement',
    },
    passed: {
      title: 'passed',
    },
    improve: 'Improve content',
    improving: 'Improving...',
    criteria: {
      short_paragraphs: 'Short paragraphs',
      bold_usage: 'Bold usage',
      lists_tables: 'Lists and tables',
      heading_hierarchy: 'Heading hierarchy',
      appropriate_length: 'Appropriate length',
      faqs_included: 'FAQs included',
      no_vague_intros: 'Concrete openings',
      data_density: 'Data density',
      entity_coverage: 'Entity coverage',
      brand_voice: 'Brand voice',
      keyword_presence: 'Main keyword present',
    },
  },
  metadata: {
    title: 'SEO Metadata',
    tabs: {
      seo: 'SEO',
      social: 'Social Media',
      schema: 'Schema.org',
    },
    fields: {
      titleTag: 'SEO Title',
      metaDescription: 'Meta Description',
      slug: 'URL Slug',
      geo: 'Geolocation',
      schemaOrg: 'Structured Data',
    },
  },
  videoImport: {
    title: 'Import from Video',
    description: 'Paste a YouTube or TikTok video URL to auto-fill the form with content extracted from the video.',
    urlPlaceholder: 'https://www.youtube.com/watch?v=... or TikTok URL',
    importButton: 'Import',
    importing: 'Analyzing video...',
    supported: 'Supported: YouTube, TikTok',
    success: {
      title: 'Form auto-filled',
      description: 'Review and edit the fields below before generating.',
    },
    progress: {
      'transcription-started': 'Transcribing video...',
      'platform-detected': 'Platform detected',
      'transcription-complete': 'Transcription complete',
      'extraction-started': 'Extracting content data...',
      'serp-enrichment-started': 'Analyzing competition...',
      'serp-enrichment-complete': 'Competition analysis complete',
    },
    errors: {
      invalidUrl: 'Please enter a valid URL',
      unsupportedPlatform: 'Only YouTube and TikTok URLs are supported',
      generic: 'Failed to import video content',
    },
  },
  textImport: {
    title: 'Ask AI',
    description: 'Tell AI what article you need, like chatting with an assistant. You can give instructions, paste text, or mix both.',
    placeholder: 'Tell AI what article you want...\n\nExample: "I need an article about digital marketing for ecommerce in Latin America. It should cover SEO, social media, and email marketing. Professional but accessible tone."',
    extractButton: 'Extract data',
    extracting: 'Extracting data...',
    extractingPhase1: 'Analyzing competition...',
    extractingPhase2: 'Generating data...',
    success: {
      title: 'Form auto-filled',
      description: 'Review and edit the fields below before generating.',
    },
    errors: {
      tooShort: 'Text must be at least 20 characters',
      tooLong: 'Text must not exceed 15,000 characters',
      generic: 'Failed to extract data from text',
    },
  },
  internalSources: {
    title: 'Internal Sources',
    description: 'Upload PDFs or add URLs from your company documentation to ensure accurate content generation.',
    dropzone: {
      label: 'Drop PDF here or click to upload',
      maxReached: 'Maximum {{max}} sources reached',
      fileSize: 'PDF files up to 10MB',
    },
    urlInput: {
      placeholder: 'https://your-company-docs.com/article',
      addButton: 'Add URL',
    },
    sourcesList: {
      title: 'Added Sources ({{count}}/{{max}})',
      readyToProcess: 'Ready to process',
    },
    toast: {
      fileTooLarge: {
        title: 'File too large',
        description: 'PDF files must be under 10MB',
      },
      invalidFileType: {
        title: 'Invalid file type',
        description: 'Only PDF files are allowed',
      },
      maxSourcesReached: {
        title: 'Maximum sources reached',
        description: 'You can only add up to {{max}} sources',
      },
      pdfAdded: {
        title: 'PDF added',
        description: '{{fileName}} will be processed when generating content',
      },
      urlRequired: {
        title: 'URL required',
        description: 'Please enter a URL',
      },
      invalidUrl: {
        title: 'Invalid URL',
        description: 'Please enter a valid URL',
      },
      urlAdded: {
        title: 'URL added',
        description: 'Content will be extracted when generating',
      },
      sourceRemoved: 'Source removed',
    },
  },
  serpAnalysis: {
    title: 'SERP Analysis Results',
    debug: '(Debug)',
    searchQuery: {
      title: 'Search Query',
    },
    searchIntent: {
      title: 'Search Intent',
      types: {
        Informational: 'Informational',
        Commercial: 'Commercial',
        Transactional: 'Transactional',
        Navigational: 'Navigational',
      },
    },
    summary: {
      title: 'Summary',
    },
    commonH2Patterns: {
      title: 'Common H2 Patterns ({{count}})',
    },
    suggestedH2Structure: {
      title: 'Suggested H2 Structure ({{count}})',
      suggestedH3s: 'Suggested H3s:',
    },
    contentGaps: {
      title: 'Content Gaps ({{count}})',
    },
    sourceUrls: {
      title: 'Source URLs ({{count}})',
    },
  },
} as const

export const contentOptimization = {
  tabs: {
    create: 'Create',
    optimize: 'Optimize',
  },
  input: {
    title: 'Optimize existing content',
    description: 'Analyze a published page and get AI-powered recommendations to improve it.',
    website: 'Website',
    websitePlaceholder: 'Select website',
    noWebsites: 'No websites configured.',
    addWebsite: 'Add',
    url: 'Page URL',
    urlPlaceholder: 'https://your-site.com/blog/article-slug',
    urlHelper: 'Enter the full URL of the page you want to optimize',
    analyzeButton: 'Analyze',
    brandVoice: 'Brand Voice',
    brandVoicePlaceholder: 'Select brand voice',
    noBrandVoices: 'No brand voices configured.',
    optional: 'Optional',
    primaryKeyword: 'Primary keyword',
    primaryKeywordPlaceholder: 'e.g. valor hora extra diurna 2026',
    primaryKeywordHelper:
      'Unlocks SEO rewrite suggestions for H1/H2/H3, body, title tag and meta description.',
    secondaryKeywords: 'Secondary keywords',
    secondaryKeywordsPlaceholder: 'Comma-separated, e.g. hora extra diurna, recargo nocturno',
    secondaryKeywordsHelper: 'Used to improve keyword variant coverage in headings and body.',
    keepEmojis: 'Keep existing emojis',
    keepEmojisHelper:
      'Preserves emojis already present in headings and body. Leave off to remove them as part of the rewrite.',
  },
  analyzing: {
    title: 'Analyzing your content...',
    extracting: 'Extracting content...',
    factUpdate: 'Analyzing outdated data...',
  },
  checklist: {
    title: 'Optimization recommendations',
    back: 'Back',
    selectedCount: '{{selected}} of {{total}} selected',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    original: 'Original',
    updated: 'Updated',
    customInstructions: 'Custom instructions (optional)',
    customInstructionsPlaceholder: 'E.g.: Keep the same tone, don\'t change the introduction...',
    customInstructionsHelper: 'Additional instructions for the AI when applying recommendations',
    applyButton: 'Apply {{count}} recommendations',
    applying: 'Applying...',
    priority: {
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    },
  },
  applying: {
    title: 'Applying optimizations...',
    description: 'AI is generating optimized content with {{count}} recommendations applied.',
    hint: 'This may take a few minutes. You\'ll be redirected when ready.',
  },
  categories: {
    fact_update: 'Fact Updates',
    keyword: 'Keyword Optimization',
    interlink_outgoing: 'Outgoing Internal Links',
    quality: 'Content Quality',
    headings_rewrite: 'Heading Rewrites',
    body_cleaning: 'Body Cleanup',
    title_tag: 'Title Tag',
    meta_description: 'Meta Description',
  },
  errors: {
    analyzeFailed: 'Failed to analyze content',
    applyFailed: 'Failed to apply optimization',
    invalidUrl: 'Enter a valid URL (e.g. https://example.com)',
  },
  success: {
    applied: 'Optimization applied successfully',
    optimized: 'Content optimized successfully!',
  },
  info: {
    title: 'How does it work?',
    description: 'Paste a published URL, get AI-powered recommendations, choose which to apply, and save the optimized version as new content in Referent.',
    currentTitle: 'What it does today',
    features: {
      interlinking: 'Suggests internal links to other pages on your site',
      qualityChecks: 'Detects AI filler phrases, dashes, formatting issues, and punctuation errors',
      customInstructions: 'Add free-text instructions to guide the optimization',
      brandVoice: 'Apply your brand voice to the optimized output',
      checklist: 'Unified checklist to review and select recommendations',
      savedAsContent: 'Saves the optimized article as new content in Referent',
    },
    v2Title: 'Coming soon',
    v2: {
      gscIntegration: 'Google Search Console integration — keyword gaps and ranking opportunities',
      inboundLinks: 'Inbound link suggestions — pages that should link to your article',
      youtubeEmbeds: 'YouTube video embeds — relevant videos from your channel',
      aiImages: 'AI-generated images — on-brand visuals for key sections',
      boldKeywords: 'Bold keywords — auto-highlight target keywords with <strong> tags',
      factUpdate: 'Fact updates — detect and replace outdated data, stats, and dates',
    },
    howItWorksTitle: 'Step by step',
    steps: {
      paste: 'Select a website and paste the URL of the page you want to optimize',
      analyze: 'The system extracts the content and runs parallel analyses',
      review: 'Review the checklist, uncheck what you don\'t want, add custom instructions',
      apply: 'Apply and the optimized article is saved as new content',
    },
  },
} as const
